import { useState } from 'react';
import { X, Flag } from 'lucide-react';
import { useFeatureFlags } from '../../../../hooks/platform-admin/useFeatureFlags';

interface CreateFeatureFlagModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateFeatureFlagModal({ onClose, onSuccess }: CreateFeatureFlagModalProps) {
  const { createFlag, isLoading } = useFeatureFlags();

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!key || !name || !category) {
      setError('Key, name, and category are required');
      return;
    }

    const result = await createFlag({
      key: key.toLowerCase().replace(/\s+/g, '_'),
      name,
      description: description || undefined,
      category,
      isGlobal,
    });

    if (result) {
      onSuccess();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg">
              <Flag className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Create Feature Flag</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Flag Key * <span className="text-xs text-gray-500">(lowercase, underscores only)</span>
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono"
              placeholder="new_feature_flag"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Unique identifier used in code (e.g., "advanced_reporting")
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Advanced Reporting"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="What does this feature flag control?"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="e.g., Features, Integrations, Experimental"
              required
              list="category-suggestions"
            />
            <datalist id="category-suggestions">
              <option value="Features" />
              <option value="Integrations" />
              <option value="Experimental" />
              <option value="Beta" />
              <option value="Premium" />
            </datalist>
          </div>

          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <input
              type="checkbox"
              id="isGlobal"
              checked={isGlobal}
              onChange={(e) => setIsGlobal(e.target.checked)}
              className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <div className="flex-1">
              <label htmlFor="isGlobal" className="text-sm font-medium text-gray-900 cursor-pointer">
                Enable Globally
              </label>
              <p className="text-xs text-gray-600 mt-1">
                If checked, this feature will be enabled for all organizations automatically.
                Otherwise, you'll need to enable it per-organization.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create Flag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
